import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { membershipTiers } from '../data/mock';
import {
  BookOpen,
  Bookmark,
  Star,
  Clock,
  Leaf,
  TreePine,
  Crown,
  Compass,
  LogOut,
  Settings,
  Heart,
  Sparkles,
  ChevronRight,
  Play,
  Menu,
  X,
  Home,
  Library,
  Lock,
  Loader2
} from 'lucide-react';

// Import API URL from utility
import { API_URL, getImageUrl } from '../lib/api';

// Get page count from various formats
const getPageCount = (book) => {
  if (book.pages && Array.isArray(book.pages)) {
    return book.pages.filter(Boolean).length;
  }
  if (book.pageImages && Array.isArray(book.pageImages)) {
    return book.pageImages.filter(Boolean).length;
  }
  if (typeof book.pages === 'number') return book.pages;
  if (typeof book.total_pages === 'number') return book.total_pages;
  return 0;
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [books, setBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(true);

  const userTier = user?.tier || 'free';
  const currentTierInfo = membershipTiers.find(t => t.id === userTier) || membershipTiers[0];
  const [userStats, setUserStats] = useState({ books_read: 0, pages_turned: 0, reading_streak: 0 });

  // Fetch books from API
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/books`);
        if (response.ok) {
          const data = await response.json();
          setBooks(data);
        }
      } catch (err) {
        console.log('Error fetching books:', err);
      }
      setLoadingBooks(false);
    };
    fetchBooks();
  }, []);

  // Fetch user stats
  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/api/user/stats`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setUserStats(data);
        } else {
          console.log('Stats fetch failed:', response.status);
        }
      } catch (err) {
        console.log('Error fetching stats:', err);
      }
    };
    if (user?.user_id) fetchStats();
  }, [user?.user_id]);

  // Load reading progress from localStorage
  const [readingProgress, setReadingProgress] = useState([]);
  
  useEffect(() => {
    if (!user?.user_id || books.length === 0) return;
    
    const progress = [];
    books.forEach(book => {
      const bookId = book.book_id || book.id;
      const progressKey = `reading_progress_${user.user_id}_${bookId}`;
      const savedProgress = localStorage.getItem(progressKey);
      
      if (savedProgress) {
        try {
          const data = JSON.parse(savedProgress);
          if (data.currentPage > 0) {
            const totalPages = book.pages?.length || 0;
            progress.push({
              bookId,
              currentPage: data.currentPage,
              totalPages,
              progress: totalPages > 0 ? Math.round((data.currentPage / totalPages) * 100) : 0,
              lastRead: data.lastRead || 'Recently'
            });
          }
        } catch (e) {
          console.log('Error parsing progress:', e);
        }
      }
    });
    
    // Sort by most recent
    progress.sort((a, b) => new Date(b.lastRead) - new Date(a.lastRead));
    setReadingProgress(progress.slice(0, 4)); // Show top 4
  }, [user?.user_id, books]);

  // Load bookmarks from localStorage
  const [bookmarks, setBookmarks] = useState([]);
  
  useEffect(() => {
    if (!user?.user_id || books.length === 0) return;
    
    const allBookmarks = [];
    books.forEach(book => {
      const bookId = book.book_id || book.id;
      const bookmarksKey = `bookmarks_${user.user_id}_${bookId}`;
      const savedBookmarks = localStorage.getItem(bookmarksKey);
      
      if (savedBookmarks) {
        try {
          const pages = JSON.parse(savedBookmarks);
          pages.forEach(page => {
            allBookmarks.push({
              bookId,
              page,
              bookTitle: book.title
            });
          });
        } catch (e) {
          console.log('Error parsing bookmarks:', e);
        }
      }
    });
    
    setBookmarks(allBookmarks.slice(0, 5)); // Show top 5
  }, [user?.user_id, books]);

  const getBookById = (id) => books.find(b => (b.book_id || b.id) === id);

  // Check if user can access a book based on tier
  // Free books: visible to all
  // Basic books: visible to basic and premium
  // Premium books: visible to premium only
  const canAccessBook = (book) => {
    const tierLevels = { free: 0, basic: 1, premium: 2 };
    const userLevel = tierLevels[userTier] || 0;
    const bookLevel = tierLevels[book.tier] || 0;
    return userLevel >= bookLevel;
  };

  // Filter books based on visibility rule:
  // Show all books, but indicate which ones are locked
  const getVisibleBooks = () => {
    return books;
  };

  const TierIcon = ({ tier }) => {
    const icons = { free: Leaf, basic: Compass, premium: Crown };
    const Icon = icons[tier] || Leaf;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-sky-50 to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              {/* Menu Toggle */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMenu(true)}
                className="text-slate-600 hover:text-emerald-600"
              >
                <Menu className="w-5 h-5" />
              </Button>
              
              <Link to="/" className="flex items-center gap-2">
                <TreePine className="w-8 h-8 text-emerald-600" />
                <span className="font-serif font-bold text-xl text-slate-800">My Library</span>
              </Link>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 rounded-full">
                <TierIcon tier={userTier} />
                <span className="text-sm font-medium text-emerald-700 capitalize">{currentTierInfo.name}</span>
              </div>
              
              <div className="flex items-center gap-3">
                <img
                  src={user?.picture || 'https://via.placeholder.com/40'}
                  alt={user?.name}
                  className="w-10 h-10 rounded-full border-2 border-emerald-200"
                />
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-slate-800">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Slide-out Menu */}
      {showMenu && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowMenu(false)} />
          <div className="fixed left-0 top-0 bottom-0 w-72 bg-white shadow-2xl z-50 flex flex-col">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2 text-emerald-600">
                <TreePine className="w-6 h-6" />
                <span className="font-serif font-bold">Creassion</span>
              </Link>
              <Button variant="ghost" size="sm" onClick={() => setShowMenu(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <img
                  src={user?.picture || 'https://via.placeholder.com/40'}
                  alt={user?.name}
                  className="w-12 h-12 rounded-full border-2 border-emerald-200"
                />
                <div>
                  <p className="font-bold text-slate-800">{user?.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{userTier} Member</p>
                </div>
              </div>
            </div>
            
            <nav className="flex-1 p-4 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                onClick={() => setShowMenu(false)}
              >
                <Home className="w-5 h-5" />
                Home
              </Link>
              <Link
                to="/dashboard"
                className="flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 text-emerald-600"
                onClick={() => setShowMenu(false)}
              >
                <Library className="w-5 h-5" />
                My Library
              </Link>
              <Link
                to="/readathon"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                onClick={() => setShowMenu(false)}
              >
                <BookOpen className="w-5 h-5" />
                Readathon
              </Link>
              <Link
                to="/upgrade"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                onClick={() => setShowMenu(false)}
              >
                <Crown className="w-5 h-5" />
                Upgrade Plan
              </Link>
              {(user?.role === 'community_manager' || user?.role === 'super_admin') && (
                <Link
                  to="/admin"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-amber-600 hover:bg-amber-50 transition-colors"
                  onClick={() => setShowMenu(false)}
                >
                  <Settings className="w-5 h-5" />
                  Admin Portal
                </Link>
              )}
            </nav>
            
            <div className="p-4 border-t border-slate-100">
              <button
                onClick={() => { logout(); setShowMenu(false); }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors w-full"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-emerald-100 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-bold text-slate-800 mb-2">
                Welcome back, {user?.name?.split(' ')[0]}! 
                <span className="inline-block animate-wave ml-2">👋</span>
              </h1>
              <p className="text-slate-600">Ready for another Bible adventure today?</p>
            </div>
            
            {userTier !== 'premium' && (
              <Button
                onClick={() => navigate('/upgrade')}
                className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white rounded-full px-6 shadow-lg group"
              >
                <Crown className="w-4 h-4 mr-2" />
                Upgrade to {userTier === 'free' ? 'Adventurer' : 'Champion'}
                <Sparkles className="w-4 h-4 ml-2 group-hover:animate-spin" />
              </Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Book Library */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-emerald-600" />
                  Book Library
                </h2>
                <Button variant="ghost" size="sm" className="text-emerald-600">
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {loadingBooks ? (
                  <div className="col-span-full text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto" />
                    <p className="text-slate-500 mt-2">Loading books...</p>
                  </div>
                ) : books.length === 0 ? (
                  <div className="col-span-full text-center py-8 bg-slate-50 rounded-2xl">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No books available yet</p>
                  </div>
                ) : (
                  getVisibleBooks().map((book) => {
                    const hasAccess = canAccessBook(book);
                    const pageCount = getPageCount(book);
                    const coverImage = getImageUrl(book.cover_url || book.cover || (book.pages && Array.isArray(book.pages) && book.pages[0]) || null);
                    const bookId = book.book_id || book.id;
                    
                    return (
                      <div
                        key={bookId}
                        className={`bg-white rounded-2xl overflow-hidden border shadow-sm hover:shadow-lg transition-all duration-300 group ${
                          hasAccess ? 'border-emerald-100 cursor-pointer' : 'border-slate-200'
                        }`}
                        onClick={() => hasAccess ? navigate(`/read/${bookId}`) : navigate('/upgrade')}
                      >
                        <div className="relative">
                          {coverImage ? (
                            <img
                              src={coverImage}
                              alt={book.title}
                              className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="w-full h-40 bg-slate-100 flex items-center justify-center">
                              <BookOpen className="w-12 h-12 text-slate-300" />
                            </div>
                          )}
                          
                          {/* Tier badge */}
                          <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                            book.tier === 'free' ? 'bg-emerald-100 text-emerald-700' :
                            book.tier === 'basic' ? 'bg-sky-100 text-sky-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            <TierIcon tier={book.tier} />
                            {book.tier === 'free' ? 'Free' : book.tier === 'basic' ? 'Adventurer' : 'Champion'}
                          </div>
                          
                          {!hasAccess && (
                            <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center">
                              <div className="bg-white/90 rounded-full px-3 py-1.5 text-xs font-medium text-slate-700 flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                Upgrade to unlock
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="p-4">
                          <h3 className="font-bold text-slate-800 mb-1 truncate">{book.title}</h3>
                          <p className="text-xs text-slate-500 mb-2">
                            {book.category || 'Faith Stories'} • {pageCount} pages
                          </p>
                          <span className="px-2 py-0.5 bg-slate-100 rounded-full text-xs text-slate-400">
                            Ages {book.age_range || '4-10'}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Bible Quiz */}
            <div 
              className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl p-5 text-white shadow-lg cursor-pointer hover:shadow-xl transition-all hover:scale-[1.02]"
              onClick={() => navigate('/quiz')}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold">Bible Quiz</h3>
                  <p className="text-purple-200 text-xs">Test your knowledge</p>
                </div>
              </div>
              <p className="text-sm text-purple-100">100 quizzes across Easy, Medium, and Hard levels. How much do you know?</p>
            </div>

            {/* Continue Reading - Right Panel */}
            {readingProgress.length > 0 && (
              <div className="bg-white rounded-2xl p-5 border border-emerald-100 shadow-sm">
                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  Continue Reading
                </h3>
                
                <div className="space-y-3">
                  {readingProgress.map((item) => {
                    const book = getBookById(item.bookId);
                    if (!book) return null;
                    return (
                      <div
                        key={item.bookId}
                        className="flex gap-3 p-2 rounded-xl hover:bg-emerald-50 transition-colors cursor-pointer group"
                        onClick={() => navigate(`/read/${book.book_id || book.id}`)}
                      >
                        <img
                          src={getImageUrl(book.cover_url || book.cover || (book.pages && Array.isArray(book.pages) && book.pages[0]))}
                          alt={book.title}
                          className="w-12 h-16 object-cover rounded-lg shadow-sm group-hover:scale-105 transition-transform"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-800 text-sm truncate">{book.title}</h4>
                          <div className="mt-1.5">
                            <div className="h-1.5 bg-emerald-100 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-emerald-500 rounded-full"
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{item.progress}% complete</p>
                          </div>
                        </div>
                        <Play className="w-4 h-4 text-emerald-500 self-center opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" fill="currentColor" />
                Your Progress
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 text-sm">Books Read</span>
                  <span className="font-bold text-emerald-600">{userStats.books_read}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 text-sm">Pages Turned</span>
                  <span className="font-bold text-emerald-600">{userStats.pages_turned}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600 text-sm">Reading Streak</span>
                  <span className="font-bold text-amber-500">{userStats.reading_streak} days {userStats.reading_streak > 0 && '🔥'}</span>
                </div>
              </div>
            </div>

            {/* Bookmarks */}
            <div className="bg-white rounded-2xl p-6 border border-emerald-100 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Bookmark className="w-5 h-5 text-coral-500" />
                My Bookmarks
              </h3>
              
              {bookmarks.length > 0 ? (
                <div className="space-y-3">
                  {bookmarks.slice(0, 3).map((bm, idx) => {
                    const book = getBookById(bm.bookId);
                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-2 rounded-xl hover:bg-emerald-50 transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 bg-coral-100 rounded-lg flex items-center justify-center">
                          <Bookmark className="w-5 h-5 text-coral-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{book?.title}</p>
                          <p className="text-xs text-slate-500">Page {bm.page}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No bookmarks yet. Start reading to add some!
                </p>
              )}
            </div>

            {/* Tier Info */}
            <div className={`rounded-2xl p-6 border shadow-sm ${
              userTier === 'free' ? 'bg-emerald-50 border-emerald-200' :
              userTier === 'basic' ? 'bg-sky-50 border-sky-200' :
              'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  userTier === 'free' ? 'bg-emerald-100' :
                  userTier === 'basic' ? 'bg-sky-100' :
                  'bg-amber-100'
                }`}>
                  <TierIcon tier={userTier} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800">{currentTierInfo.name}</h4>
                  <p className="text-xs text-slate-600">{currentTierInfo.description}</p>
                </div>
              </div>
              
              {userTier !== 'premium' && (
                <Button
                  onClick={() => navigate('/upgrade')}
                  variant="outline"
                  className="w-full rounded-full text-sm"
                >
                  Upgrade Now
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
